// Image Upload Component für Supabase Storage

import { useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import Button from './Button';
import toast from 'react-hot-toast';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  bucket?: string;
  folder?: string;
  label?: string;
  accept?: string;
  maxSizeMB?: number;
}

export default function ImageUpload({
  value,
  onChange,
  bucket = 'logos',
  folder = 'customers',
  label = 'Logo hochladen',
  accept = 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml',
  maxSizeMB = 5,
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Größenprüfung
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Datei ist zu groß. Maximum: ${maxSizeMB}MB`);
      return;
    }

    // Typ-Prüfung
    if (!accept.split(',').some(type => file.type === type.trim())) {
      toast.error('Ungültiger Dateityp. Erlaubt: JPG, PNG, GIF, WebP, SVG');
      return;
    }

    setIsUploading(true);
    setPreviewError(false);

    try {
      // Eindeutigen Dateinamen generieren
      const fileExt = file.name.split('.').pop();
      const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Hochladen zu Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Öffentliche URL holen
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      onChange(publicUrl);
      toast.success('Logo erfolgreich hochgeladen!');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Fehler beim Hochladen');
    } finally {
      setIsUploading(false);
      // Input zurücksetzen
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = async () => {
    if (!value) return;

    // Optional: Datei aus Storage löschen
    try {
      // URL parsen um Dateiname zu extrahieren
      const urlParts = value.split('/');
      const fileName = urlParts.slice(-2).join('/'); // folder/filename.ext
      
      await supabase.storage.from(bucket).remove([fileName]);
    } catch (error) {
      console.warn('Could not delete file from storage:', error);
    }

    onChange('');
    setPreviewError(false);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {/* Vorschau */}
      {value && !previewError && (
        <div className="relative inline-block">
          <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
            <img
              src={value}
              alt="Logo Vorschau"
              className="max-h-20 max-w-[200px] object-contain"
              onError={() => setPreviewError(true)}
            />
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
            title="Logo entfernen"
          >
            ×
          </button>
        </div>
      )}

      {/* Fehler bei Vorschau */}
      {value && previewError && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
          ⚠️ Bild konnte nicht geladen werden. 
          <button 
            type="button"
            onClick={handleRemove}
            className="ml-2 underline"
          >
            Entfernen
          </button>
        </div>
      )}

      {/* Upload Button */}
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
          id="logo-upload"
        />
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          loading={isUploading}
        >
          {isUploading ? 'Lädt hoch...' : value ? '🔄 Logo ändern' : '📤 Logo hochladen'}
        </Button>
        
        <span className="text-xs text-gray-500">
          JPG, PNG, GIF, WebP, SVG • Max. {maxSizeMB}MB
        </span>
      </div>
    </div>
  );
}
